import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Typography,
  Grid,
  Box,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
} from '@mui/material'
import { toast } from 'react-toastify'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'

const DocumentList = ({
  data,
  title,
  basePath,
  iconPath,
  navigatePath,
  noFilesMessage = 'No files available',
}) => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')

  const filteredData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      documents: item.documents?.filter(
        (file) =>
          file.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (file.description &&
            file.description.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    }))
  }, [data, searchTerm])

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop().toLowerCase()
    switch (ext) {
      case 'pdf':
        return iconPath
          ? basePath + iconPath
          : basePath + 'DOCTYPE_ICONS/PDF_Icon.svg'
      case 'docx':
        return iconPath
          ? basePath + iconPath
          : basePath + 'DOCTYPE_ICONS/Doc_Icon.svg'
      default:
        return iconPath
          ? basePath + iconPath
          : basePath + 'DOCTYPE_ICONS/empty_icon.svg'
    }
  }

  const handleFileClick = (file) => {
    if (file.externalLink) {
      const isAbsoluteUrl = /^https?:\/\//i.test(file.externalLink)

      const fullUrl = isAbsoluteUrl
        ? file.externalLink
        : window.location.origin + file.externalLink

      window.open(fullUrl, '_blank')
      return
    }

    if (!file.pathurl) {
      toast.error('There is no file', {
        position: toast.POSITION.TOP_RIGHT,
      })
      return
    }

    navigate(`${navigatePath}/${file.ID}/${file.filename}`, {
      state: { selectedDocData: file, fromList: navigatePath },
    })
  }

  const handleClearSearch = () => {
    setSearchTerm('')
  }

  return (
    <Box sx={{ p: 3 }}>
      {filteredData.map((item, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 4,
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                textAlign: 'right',
                color: 'primary.main',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bottom: -8,
                  borderRadius: 2,
                },
              }}
            >
              {title} {item.Year ? `(${item.Year})` : ''}
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 300,
              }}
            >
              <TextField
                fullWidth
                placeholder="Search documents..."
                value={searchTerm}
                variant="standard"
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'background.default',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClearSearch}
                        size="small"
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Grid container spacing={3}>
            {item.documents && item.documents.length > 0 ? (
              item.documents.map((file, idx) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                  <Box
                    onClick={() => handleFileClick(file)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      height: 240,
                      p: 3,
                      borderRadius: 3,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 1,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: 3,
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: 120,
                        mb: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={getFileIcon(basePath + file.pathurl)}
                        alt="File Icon"
                        loading="lazy"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>

                    <Tooltip title={file.filename} placement="top" arrow>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          textAlign: 'center',
                          color: 'text.primary',
                          width: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {file.filename}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: 'center',
                    color: 'text.secondary',
                    py: 4,
                  }}
                >
                  {searchTerm
                    ? 'No documents match your search criteria'
                    : noFilesMessage}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      ))}
    </Box>
  )
}

export default DocumentList
